import { AffiliateTypeEnum } from '@adminPortal/_common/_enums/affiliate-type';
import { COUNTRIES_PASHA } from '@adminPortal/_common/_enums/countries-pasha';
import { Languages } from '@adminPortal/_common/_enums/languages';
import { Utils } from '@adminPortal/_common/_helpers/utils';
import { IAffiliate } from '@adminPortal/_common/_interfaces/IAffiliate';
import { IClientPostbacks } from '@adminPortal/_common/_interfaces/IClientPostbacks';
import { IDomain } from '@adminPortal/_common/_interfaces/IDomain';
import { IGetAffiliateListRequestData } from '@adminPortal/_common/_interfaces/IGetAffiliateListRequestData';
import { IGetOffersListRequestData } from '@adminPortal/_common/_interfaces/IGetOffersListRequestData';
import { IGetOffersListResponseData } from '@adminPortal/_common/_interfaces/IGetOffersListResponseData';
import { IOffer } from '@adminPortal/_common/_interfaces/IOffer';
import { IPwaContent } from '@adminPortal/_common/_interfaces/IPwaContent';
import { ITransaction } from '@adminPortal/_common/_interfaces/ITransaction';
import { ITranslationResponse } from '@adminPortal/_common/_interfaces/ITranslateContentResponse';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { HttpEndpoints } from '../../_enums/http-endpoints';
import { IDesign } from '../../_interfaces/IDesign';
import { IPwaPreviewConfig } from '../../_interfaces/IPwaPreviewConfig';
import { IUser } from '../../_interfaces/IUser';
import { API_URL, BRANCH_ID } from '../../_tokens/tokens';
import { AuthResponse } from '@adminPortal/_common/_interfaces/IAuthResponse';

@Injectable({
  providedIn: 'root',
})
export class DataProviderService {
  constructor(
    @Inject(API_URL) private apiUrl: string,
    @Inject(BRANCH_ID) private branchId: string,
    private http: HttpClient
  ) {}

  private buildUrl(path: HttpEndpoints | string, pathParams?: Record<string, string | number>): string {
    if (pathParams) {
      const paramKeys = Object.keys(pathParams);

      paramKeys.forEach((key: string) => {
        path = path?.replace(`{${key}}`, pathParams[key].toString());
      });
    }

    return `${this.apiUrl}${path}`;
  }

  private buildHttpParams(queryParams: Record<string, string | number>): HttpParams {
    const paramKeys = Object.keys(queryParams);
    let params = new HttpParams();

    paramKeys.forEach((key: string) => {
      if (queryParams[key]) {
        params = params.append(key, queryParams[key]);
      }
    });

    return params;
  }

  // Public methods
  public login(login: string, password: string): Observable<AuthResponse> {
    const url = this.buildUrl(HttpEndpoints.LOGIN);
    const body = { username: login, password, branchId: this.branchId };
    return this.http.post<AuthResponse>(url, body);
  }

  public refreshToken(refreshToken: string): Observable<AuthResponse> {
    const url = this.buildUrl(HttpEndpoints.REFRESH_TOKEN);
    return this.http.post<AuthResponse>(url, { refreshToken });
  }

  public signUp(username: string, password: string, inviteId: string, token: string): Observable<AuthResponse> {
    const url = this.buildUrl(HttpEndpoints.REGISTRATION);
    return this.http.post<AuthResponse>(url, { username, password, inviteId, token });
  }

  public register(
    username: string,
    email: string,
    password: string,
    type: AffiliateTypeEnum
  ): Observable<{ token: string }> {
    const url = this.buildUrl(HttpEndpoints.REGISTRATION_COMMON);
    return this.http.post<{ token: string }>(url, {
      branchId: this.branchId,
      username,
      email,
      password,
      type,
    });
  }

  public confirmRegistration(userId: string, token: string): Observable<AuthResponse> {
    const url = this.buildUrl(HttpEndpoints.REGISTRATION_COMMON_CONFIRM);
    return this.http.post<AuthResponse>(url, { userId, token });
  }

  public getUser(): Observable<IUser> {
    const url = this.buildUrl(HttpEndpoints.USER);
    return this.http.get<any>(url);
  }

  // PWA
  public getPwaList(): Observable<IPwaPreviewConfig[]> {
    const url = this.buildUrl(HttpEndpoints.GET_PWA_LIST);

    return this.http.get<any>(url).pipe(
      map((response) => {
        return response.member;
      })
    );
  }

  public createNewPwa(pwa: IPwaPreviewConfig): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.CREATE_PWA_ITEM);

    const body = this.formatPwaToBackend(pwa);

    return this.http.post<any>(url, body).pipe(map((response) => this.formatPwaFromBackend(response)));
  }

  public deletePwa(pwa: IPwaPreviewConfig): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.DELETE_PWA_ITEM, { id: pwa.id });

    return this.http.delete<any>(url);
  }

  public activatePwa(pwa: IPwaPreviewConfig): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.ACTIVATE_PWA_ITEM, { id: pwa.id });

    return this.http.post<any>(url, {}).pipe(map((response) => this.formatPwaFromBackend(response)));
  }

  public deactivatePwa(pwa: IPwaPreviewConfig): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.DEACTIVATE_PWA_ITEM, { id: pwa.id });

    return this.http.post<any>(url, {}).pipe(map((response) => this.formatPwaFromBackend(response)));
  }

  public clonePwa(pwa: IPwaPreviewConfig): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.CLONE_PWA_ITEM, { id: pwa.id });

    return this.http.put<any>(url, {});
  }

  public loadPwa(id: string): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.GET_PWA_ITEM, { id });

    return this.http.get<any>(url).pipe(map((response) => this.formatPwaFromBackend(response)));
  }

  public savePwa(pwa: IPwaPreviewConfig): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.SAVE_PWA_ITEM, { id: pwa.id });
    const headers = { 'Content-Type': 'application/merge-patch+json' };
    const body = this.formatPwaToBackend(pwa);

    return this.http.patch<any>(url, body, { headers }).pipe(map((response) => this.formatPwaFromBackend(response)));
  }

  public uploadImage(images: any) {
    const url = this.buildUrl(HttpEndpoints.UPLOAD_IMAGE);
    return this.http.post<any>(url, images).pipe(map((response) => response.uploads[0].image));
  }

  public loadDesigns(vertical: string): Observable<IDesign[]> {
    const url = this.buildUrl(HttpEndpoints.DESIGN);
    const params = this.buildHttpParams({ vertical });
    return this.http.get<any>(url, { params }).pipe(map((response) => response.member));
  }

  public loadDomains(): Observable<IDomain[]> {
    const url = this.buildUrl(HttpEndpoints.GET_DOMAINS);
    return this.http.get<any>(url).pipe(map((response) => response.member));
  }

  public acquireDomain(): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.ACQUIRE_DOMAIN);
    return this.http.put<any>(url, {}).pipe(map((response) => response));
  }

  public translatePwaContent(
    fromLanguage: Languages,
    toLanguages: Languages[],
    content: IPwaContent,
    id: string
  ): Observable<ITranslationResponse> {
    const url = this.buildUrl(HttpEndpoints.TRANSLATE_PWA_CONTENT, { id });
    const body = {
      fromLanguage,
      toLanguages,
      content: this.mapContentForTranslation(content),
      fromContent: this.mapContentForTranslation(content),
      resultContents: ['string'],
      isCompleted: true,
    };

    return this.http.post<ITranslationResponse>(url, body).pipe(
      tap((response) => {
        console.log({ response });
      })
    );
  }

  public getTranslatedPwaContent(id: string): Observable<ITranslationResponse> {
    const url = this.buildUrl(HttpEndpoints.TRANSLATE_PWA_CONTENT, { id });
    return this.http.get<ITranslationResponse>(url);
  }

  private mapContentForTranslation(content: IPwaContent): any {
    return {
      comments:
        content.comments?.map((comment) => ({
          avatarImageId: comment.avatar?.id || null,
          answerAvatarImageId: comment.answerAvatar?.id || null,
          author: comment.author || '',
          commentContent: comment.commentContent || '',
          date: comment.date
            ? {
                dateTime: comment.date?.dateTime,
                type: comment.date?.type,
                offsetInDays: comment.date?.offsetInDays,
              }
            : null,
          rating: comment.rating || 0,
          likes: comment.likes || 0,
          answerAuthor: comment.answerAuthor || '',
          answerContent: comment.answerContent || '',
          answerDate: comment.answerDate
            ? {
                dateTime: comment.answerDate?.dateTime,
                offsetInDays: comment.answerDate?.offsetInDays,
                type: comment.answerDate?.type,
              }
            : null,
        })) || [],
      isDefault: content.isDefault,
      name: content.name || '',
      developer: content.developer || '',
      ad: content.ad || '',
      category: content.category || '',
      age: content.age || '',
      size: content.size || '',
      description: content.description || '',
      update: content.update || '',
      updateDate: content.updateDate
        ? {
            dateTime: content.updateDate?.dateTime,
            offsetInDays: content.updateDate?.offsetInDays,
            type: content.updateDate?.type,
          }
        : null,
      keywords: content.keywords || [],
      rating: content.rating || 0,
      feedbacksAmount: content.feedbacksAmount || '',
      uploadsAmount: content.uploadsAmount || '',
      version: content.version || '',
    };
  }

  // Offer-url
  public loadOffersList(requestData: IGetOffersListRequestData): Observable<IGetOffersListResponseData> {
    const url = this.buildUrl(HttpEndpoints.GET_OFFER_LIST);
    const params = this.buildHttpParams({ ...requestData });

    return this.http.get<any>(url, { params }).pipe(
      map((response: any) => {
        return {
          total: response.totalItems,
          offersList: response.member.map((el: any) => ({
            ...el,
            created: el.created * 1000,
            countries: el.countries.map((c: any) => c.alpha2Code),
          })),
        } as IGetOffersListResponseData;
      })
    );
  }

  public createNewOffer(offer: IOffer): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.CREATE_OFFER);

    const body = {
      name: offer.name,
      url: offer.url,
      countries: offer.countries.map((c) => COUNTRIES_PASHA.member.find((el) => el.alpha2Code == c)?.id),
    };

    return this.http.post<any>(url, { ...body });
  }

  public deleteOffer(offer: IOffer): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.DELETE_OFFER, { id: offer.id });

    return this.http.delete<any>(url);
  }

  public loadOffer(id: string): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.GET_OFFER, { id });

    return this.http.get<any>(url).pipe(
      map((response: any) => {
        return {
          ...response,
          countries: response.countries.map((c: any) => c.alpha2Code),
        };
      })
    );
  }

  public saveOffer(offer: IOffer): Observable<any> {
    const id = offer.id || '';
    const url = this.buildUrl(HttpEndpoints.UPDATE_OFFER, { id });
    const headers = { 'Content-Type': 'application/merge-patch+json' };

    const body = {
      name: offer.name,
      url: offer.url,
      countries: offer.countries.map((c) => COUNTRIES_PASHA.member.find((el) => el.alpha2Code == c)?.id),
    };

    return this.http.patch<any>(url, { ...body }, { headers });
  }

  // Tracking
  public loadFacebookTrackingList(): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.GET_FACEBOOK_PIXELS_LIST);

    return this.http.get<any>(url).pipe(map((response) => response.member));
  }

  public getFacebookTokenData(input_token: string, access_token: string): Observable<any> {
    const url = HttpEndpoints.GET_FACEBOOK_TOKEN_DATA;
    const params = this.buildHttpParams({ input_token, access_token });
    return this.http.get<any>(url, { params }).pipe(map((response) => response.data));
  }

  public createNewFacebookTracking(accessToken: string, pixelId: string): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.CREATE_FACEBOOK_PIXEL);

    const body = {
      accessToken,
      pixelId,
    };

    return this.http.post<any>(url, { ...body });
  }

  public saveFacebookTracking(accessToken: string, id: string): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.UPDATE_FACEBOOK_PIXEL, { id });

    const body = {
      accessToken,
    };

    return this.http.patch<any>(url, { ...body });
  }

  public deleteFacebookTracking(id: string): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.DELETE_FACEBOOK_PIXEL, { id });
    return this.http.delete<any>(url);
  }

  // Affiliate
  public loadAffiliateList(requestData: IGetAffiliateListRequestData): Observable<IAffiliate[]> {
    const url = this.buildUrl(HttpEndpoints.AFFILIATE_LIST);
    const params = this.buildHttpParams({ ...requestData });

    return this.http.get<any>(url, { params }).pipe(map((response) => response.member));
  }

  public createNewAffiliate(email: string, type: string): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.CREATE_INVITE);
    const headers = { 'Content-Type': 'application/ld+json' };

    return this.http.post<any>(url, { email, type }, { headers });
  }

  public deleteAffiliate(affiliate: IAffiliate): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.DELETE_INVITE);

    return this.http.post<any>(url, { affiliate });
  }

  public saveAffiliate(affiliate: IAffiliate): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.SAVE_AFFILIATE, { id: affiliate.id });
    const headers = { 'Content-Type': 'application/merge-patch+json' };

    const body = {
      name: affiliate.name,
      installPrice: affiliate.installPrice?.toString(),
    };

    return this.http.patch<any>(url, { ...body }, { headers });
  }

  public affiliateTransferBalance(amount: number, id: string): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.AFFILIATE_TRANSFER_BALANCE, { id });
    const headers = { 'Content-Type': 'application/merge-patch+json' };

    const body = {
      amount: amount.toString(),
    };

    return this.http.patch<any>(url, { ...body }, { headers });
  }

  // Transactions
  public loadTransactions(affiliateId: string): Observable<ITransaction[]> {
    const url = this.buildUrl(HttpEndpoints.GET_TRANSACTIONS_BY_AFFILIATE, { id: affiliateId });
    return this.http.get<any>(url).pipe(map((response) => response.member));
  }

  public loadMyTransactions(): Observable<ITransaction[]> {
    const url = this.buildUrl(HttpEndpoints.GET_MY_TRANSACTIONS);

    return this.http.get<any>(url).pipe(map((response) => response.member));
    // return this.http.get<any>('/api/client/transactions/me').pipe(
    //   map((response) => response.member)
    // );
  }

  // Statistics
  public getStatistics(startDate: string, endDate: string): Observable<any> {
    console.log('e');
    const url = this.buildUrl(HttpEndpoints.GET_STATISTICS);
    const body = {
      dateRange: {
        startDate,
        endDate,
      },
    };

    return this.http.post<any>(url, body);
  }

  // Postback
  public getClientPostbacks(): Observable<IClientPostbacks> {
    const url = this.buildUrl(HttpEndpoints.CLIENT_POSTBACKS);
    return this.http.get<IClientPostbacks>(url);
  }

  public saveClientPostbacks(payload: IClientPostbacks): Observable<IClientPostbacks> {
    const url = this.buildUrl(HttpEndpoints.CLIENT_POSTBACKS);
    return this.http.post<IClientPostbacks>(url, payload);
  }

  // Push
  public uploadPushFile(fileName: string, fileData: string): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.UPLOAD_PUSH_FILE);
    const form: FormData = new FormData();
    form.append('csv', Utils.dataURItoBlob(fileData), fileName);
    return this.http.post<any>(url, form);
  }

  public getLatestPushFile(): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.GET_LATEST_PUSH_FILE);
    return this.http.get<any>(url);
  }

  public deletePushFile(): Observable<any> {
    const url = this.buildUrl(HttpEndpoints.DELETE_PUSH_FILE);
    return this.http.delete<any>(url);
  }

  private formatPwaContentFromBackend(content: any): IPwaContent[] {
    return Object.keys(content)
      .map((key) => ({
        ...content[key],
        updateDate: {
          ...content[key].updateDate,
          dateTime: content[key].updateDate?.dateTime ? content[key].updateDate.dateTime.split('T')[0] : null,
        },
        languageCode: key,
        comments: content[key].comments?.length
          ? content[key].comments.map((el: any) => ({
              ...el,
              date: {
                ...el.date,
                dateTime: el.date?.dateTime ? el.date.dateTime.split('T')[0] : null,
              },
              answerDate: {
                ...el.answerDate,
                dateTime: el.answerDate?.dateTime ? el.answerDate.dateTime.split('T')[0] : null,
              },
            }))
          : [],
      }))
      .sort((a, b) => (a.isDefault ? -1 : 1));
  }

  private formatPwaContentToBackend(content: IPwaContent[]): any {
    const formated: any = {};

    Object.values(Languages).forEach((code) => {
      const el = content.find((el) => el.languageCode === code);
      if (el) {
        formated[el.languageCode] = structuredClone(el);
        formated[el.languageCode].comments = formated[el.languageCode].comments.map((el: any) => ({
          ...el,
          answerAvatarImageId: el.answerAvatar?.id || null,
          avatarImageId: el.avatar?.id || null,
        }));
      } else {
        formated[code] = null;
      }
    });

    return formated;

    // const formated: any = {};
    // content.forEach((el) => {
    //   formated[el.languageCode] = structuredClone(el);
    //   formated[el.languageCode].comments = formated[el.languageCode].comments.map((el: any) => ({
    //     ...el,
    //     answerAvatarImageId: el.answerAvatar?.id || null,
    //     avatarImageId: el.avatar?.id || null,
    //   }));
    // });
    //
    // return formated;
  }

  private formatPwaFromBackend(pwa: any): IPwaPreviewConfig {
    return {
      ...pwa,
      content: this.formatPwaContentFromBackend(pwa.content),
      offer: pwa.offer?.id || null,
    };
  }

  private formatPwaToBackend(pwa: IPwaPreviewConfig): any {
    const { id, content, ...body } = pwa as any;

    body.headerVideoUrl = pwa.headerVideoUrl || null;
    body.logo = pwa.logo?.id || null;
    body.domain = pwa.domain?.id || null;
    body.banners = pwa.banners ? pwa.banners.map((el) => el.id).filter((el) => !!el) : [];
    body.content = this.formatPwaContentToBackend(pwa.content);

    return body;
  }
}
